import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Link } from "react-router-dom";
import moment from "moment";
import { getBlogs } from "../../services/BlogService";

function getBlogTitle(blog) {
  return blog?.blog_subject || blog?.meta_title || "Blog Title";
}

const BlogsSection = () => {
  const sectionRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const { data: blogResponse, isLoading } = useQuery({
    queryKey: ["blogs"],
    queryFn: getBlogs,
  });

  const blogs = (blogResponse?.data || []).slice(0, 6);
  const blogCount = blogs.length;
  const shouldUseSlider = isMobile ? blogCount > 2 : blogCount >= 4;

  const sliderSettings = {
    dots: false,
    infinite: blogCount > 4,
    speed: 500,
    slidesToShow: Math.min(4, Math.max(1, blogCount || 1)),
    slidesToScroll: 1,
    autoplay: shouldUseSlider,
    autoplaySpeed: 3000,
    arrows: shouldUseSlider,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: Math.min(2, Math.max(1, blogCount || 1)),
          arrows: blogCount > 1,
          infinite: blogCount > 2,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: Math.min(2, Math.max(1, blogCount || 1)),
          arrows: blogCount > 1,
          infinite: blogCount > 1,
        },
      },
    ],
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 600px)");
    const updateIsMobile = () => setIsMobile(mediaQuery.matches);

    updateIsMobile();
    mediaQuery.addEventListener("change", updateIsMobile);

    return () => {
      mediaQuery.removeEventListener("change", updateIsMobile);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("active");
          } else {
            e.target.classList.remove("active");
          }
        });
      },
      { threshold: 0.2 }
    );
    const el = sectionRef.current;
    if (el) observer.observe(el);
    return () => {
      if (el) observer.unobserve(el);
    };
  }, []);

  if (!isLoading && blogs.length === 0) {
    return null;
  }

  const renderBlogCard = (blog) => (
    <div className="hp-blog-card">
      <span className="hp-blog-label">BLOGS</span>
      {blog.file ? (
        <img
          className="hp-blog-media"
          src={blog.file}
          alt={getBlogTitle(blog)}
          loading="lazy"
        />
      ) : (
        <div className="hp-blog-placeholder hp-blog-media">
          Kuku Namkeen
        </div>
      )}
      <div className="hp-blog-body">
        <h5
          className="blog-title"
          style={{ color: "var(--kuku-dark)" }}
        >
          {getBlogTitle(blog)}
        </h5>
        <span className="text-sm" style={{ color: "var(--kuku-dark)" }}>
          {moment(blog.created_at).isValid()
            ? moment(blog.created_at).format("MMM YYYY")
            : ""}
        </span>
        <br />
        <Link to={`/blogs/${blog.blog_id}`} className="hp-btn-link">
          Read Full Blog
        </Link>
      </div>
    </div>
  );

  return (
    <section className="hp-blogs hp-section hp-reveal" ref={sectionRef}>
      <div className="w-full">
        {/* Title */}
        <div className="max-w-[1320px] mx-auto px-4">
          <div className="hp-blogs-title">
            <h2
              className="font-bold uppercase"
              style={{ color: "var(--kuku-dark)" }}
            >
              The Kuku Chronicle
            </h2>
            <Link to="/blogs" className="blog-btn">
              View all Blogs
            </Link>
          </div>
        </div>

        {/* Slider */}
        <div className="w-full px-4">
          {isLoading ? (
            <div className="hp-blog-loading">Loading latest blogs...</div>
          ) : !shouldUseSlider ? (
            <div className="hp-blog-static-grid">
              {blogs.map((blog) => (
                <div key={blog.blog_id} className="hp-blog-static-item">
                  {renderBlogCard(blog)}
                </div>
              ))}
            </div>
          ) : (
            <Slider {...sliderSettings}>
              {blogs.map((blog) => (
                <div key={blog.blog_id}>
                  {renderBlogCard(blog)}
                </div>
              ))}
            </Slider>
          )}
        </div>
      </div>
    </section>
  );
};

export default BlogsSection;
